import { yupResolver } from "@hookform/resolvers/yup";
import { If } from "components";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { t } from "i18next";
import { useForm, FormProvider, Resolver } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, LoaderScreen } from "ui";

import { productValidationSchema } from "../../../helpers";
import {
  useProductDetailsFormSubmit,
  useProductVariantsFormFields
} from "../../../hooks";
import { AiCopyAssistantBlock } from "../../AiCopyAssistantBlock";
import {
  ColorsBlock,
  DescriptionAndSizesBlock,
  ProductDetailsAttributesBlock,
  VariantBlock,
  ProductVideoBlock,
  RealVideoBlock,
  AdditionalAttributesBlock,
  ChoiceBlock,
  BothTogetherMethodBlock
} from "./FormBlocks";
import {
  ProductDetailsFormProps,
  ProductFormValues
} from "./ProductDetailsForm.types";

import styles from "./ProductDetailsForm.module.scss";

const ProductDetailsForm = ({
  defaultValues,
  categoryAttributes: {
    sizeAttribute,
    colorAttribute,
    additionalAttributes,
    productDetailsAttributes
  }
}: ProductDetailsFormProps) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const formMethods = useForm<ProductFormValues>({
    defaultValues,
    resolver: yupResolver(productValidationSchema) as Resolver<any, any>
  });

  const { control, handleSubmit, watch } = formMethods;

  const { variantFields, isLoadingSKU } = useProductVariantsFormFields(
    watch,
    control
  );

  const { isSubmitLoading, onSubmit } =
    useProductDetailsFormSubmit(defaultValues);

  const handleCancelClick = () => {
    if (state?.canGoBack) {
      navigate(-1);
    } else {
      navigate("/products");
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form className={styles.productForm} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.productForm__line} />

        <AiCopyAssistantBlock />

        <DescriptionAndSizesBlock
          defaultValues={defaultValues}
          sizeAttribute={sizeAttribute}
        />

        <ProductVideoBlock />

        <RealVideoBlock />

        <ChoiceBlock />

        <div className={styles.productForm__block}>
          <ProductDetailsAttributesBlock
            productDetailsAttributes={productDetailsAttributes}
          />

          <If condition={additionalAttributes.length !== 0}>
            <AdditionalAttributesBlock
              additionalAttributes={additionalAttributes}
            />
          </If>

          <BothTogetherMethodBlock />
        </div>

        <div className={styles.productForm__line} />

        <ColorsBlock
          colorAttribute={colorAttribute}
          defaultValues={defaultValues}
        />

        <div className={styles.productForm__line} />

        {variantFields.map((variantItem, index) => (
          <VariantBlock
            key={variantItem.colorAttributeValueId}
            defaultValues={defaultValues}
            colorLabel={
              colorAttribute.values.find(
                (colorValue) =>
                  colorValue.id === variantItem.colorAttributeValueId
              )?.value ?? "Unknown"
            }
            sku={variantItem.sku}
            shortId={variantItem.shortId}
            index={index}
          />
        ))}

        <div className={styles.productForm__buttons}>
          <Button variant="default" type="button" onClick={handleCancelClick}>
            {t("Cancel")}
          </Button>
          <Button type="submit">{t("Submit")}</Button>
        </div>
      </form>

      <If condition={isSubmitLoading || isLoadingSKU}>
        <LoaderScreen />
      </If>
    </FormProvider>
  );
};

export default ProductDetailsForm;
